<template>
  <BaseInput
    class="EmailComponent"
    :id="id"
    :label="label"
    v-model="email"
    :placeholder="placeholder"
    :rules="validations"
    icon="icon-email1"
    :loading="isWaitingService"
    :serviceValidationError="serviceValidation.error"
    :serviceValidationMessage="serviceValidation.message"
    :inputmode="InputmodeTypes.Email"
    :inputType="InputTypes.Email"
    @focus="onFocus"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useI18n } from 'vue-i18n'
import { validateEmail } from '@/utils/validations.utils'
import type { ValidationRuleType } from '@/modules/core/models/types'
import InputmodeTypes from '@/modules/core/models/InputmodeTypes'
import InputTypes from '@/modules/core/models/InputTypes'

const commonStore = useCommonStore()
const { t } = useI18n()

interface PropsInterface {
  id: string
  modelValue: string | null
  rules: ValidationRuleType[]
  placeholder: string
  label?: string
}
const props = withDefaults(defineProps<PropsInterface>(), {
  modelValue: '',
  rules: () => [],
  placeholder: ''
})

const emit = defineEmits(['update:modelValue'])

const validationRules = {
  validEmail: (value: string) => validateEmail(value) || t('common.validations.email')
}

const validations = [...props.rules, validationRules.validEmail]

const isWaitingService = ref(false)
const serviceValidation = ref({ error: false, message: '' })

const email = computed({
  get: () => props.modelValue ?? '',
  set: (value: string) => emit('update:modelValue', value)
})

function onFocus(focussed: boolean) {
  if (focussed) return

  validateEmailWithService(email.value)
}

async function validateEmailWithService(email: string) {
  if (!validateEmail(email)) return

  isWaitingService.value = true
  const valid = await commonStore.validateEmail(email)
  isWaitingService.value = false

  serviceValidation.value.error = !valid
  serviceValidation.value.message = serviceValidation.value.error
    ? t('common.validations.email')
    : ''
}
</script>
